import { motion } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Smartphone, MonitorCheck, Download, Bell, Shield, Clock, BarChart3, MessageSquare, BellRing, LucideIcon } from 'lucide-react';

export const ClientManagementSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1); // Reduced from 0.3 for better mobile visibility

  const features = [
    {
      icon: Smartphone,
      title: 'WhatsApp Updates',
      description: 'Receive real-time case updates, document notifications, and instant responses directly through WhatsApp.',
      highlight: 'AI-Powered Alerts'
    },
    {
      icon: MonitorCheck,
      title: 'Client Portal Access',
      description: 'Login 24/7 to track case progress, view milestones, and monitor all legal matters in one secure dashboard.',
      highlight: 'Always Accessible'
    },
    {
      icon: Download,
      title: 'Document Download',
      description: 'Instantly download contracts, certificates, reports, and legal documents anytime from your portal.',
      highlight: 'Secure & Instant'
    },
    {
      icon: BarChart3,
      title: 'Progress Tracking',
      description: 'Visual timeline of your case with clear milestones, completion percentages, and estimated timelines.',
      highlight: 'Full Transparency'
    }
  ];

  
type CrmBenefit = {
  label: string;
  value: string;
  icon: LucideIcon;
};

const crmBenefits: CrmBenefit[] = [
  {
    label: 'AI-Driven Notifications',
    value: 'Smart alerts based on case activity',
    icon: BellRing,
  },
  {
    label: 'Multi-Device Access',
    value: 'Desktop, mobile, and tablet ready',
    icon: Smartphone,
  },
  {
    label: 'Secure Encryption',
    value: 'Bank-level security standards',
    icon: Shield
  },
  {
    label: '24/7 Availability',
    value: 'Access your data anytime',
    icon: Clock,
  },
];

  return (
    <section className="relative bg-[#0a0a0a] py-16 sm:py-20 lg:py-24 overflow-hidden w-full">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(195, 165, 101, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(195, 165, 101, 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}></div>
      </div>

      {/* Gradient accents */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#c3a565]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#c3a565]/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" ref={ref}>
        {/* Section Header */}
        <div className="mb-12 sm:mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4 mb-4"
          >
            <div className="h-px w-16 bg-[#c3a565]"></div>
            <span className="text-xs tracking-[0.3em] uppercase text-[#c3a565]" style={{ fontWeight: 600 }}>
              Client Management
            </span>
            <div className="h-px w-16 bg-[#c3a565]"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-[#faf9f7] text-3xl sm:text-4xl lg:text-5xl mb-4"
            style={{ 
               fontWeight: 600 }}
          >
            AI-Integrated CRM System
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-[#faf9f7]/60 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed"
            style={{ fontWeight: 300 }}
          >
            Stay connected with your legal matters through our cutting-edge client management platform. 
            Get WhatsApp updates, access your portal anytime, and download documents with complete transparency.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="bg-[#faf9f7]/5 backdrop-blur-sm border border-[#c3a565]/15 rounded-2xl p-6 hover:bg-[#faf9f7]/10 hover:border-[#c3a565]/30 transition-all duration-500 group"
              >
                {/* Icon */}
                <div className="mb-4 flex items-start justify-between">
                  <div className="w-12 h-12 bg-[#c3a565]/10 rounded-xl flex items-center justify-center group-hover:bg-[#c3a565]/20 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#c3a565]" />
                  </div>
                  <span className="text-[#c3a565] text-xs px-2.5 py-1 bg-[#c3a565]/10 rounded-full" style={{ fontWeight: 600 }}>
                    {feature.highlight}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-[#faf9f7] mb-2 text-lg" style={{ 
                   fontWeight: 600 }}>
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-[#faf9f7]/60 text-sm leading-relaxed" style={{ fontWeight: 300 }}>
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CRM Benefits Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="bg-gradient-to-r from-[#c3a565]/10 via-[#c3a565]/5 to-[#c3a565]/10 border-t border-b border-[#c3a565]/20 py-6 rounded-xl"
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
  {crmBenefits.map((benefit, index) => {
    const Icon = benefit.icon;

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={isVisible ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
        className="text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-1.5">
          <Icon className="w-4 h-4 text-[#c3a565]" />
          <p
            className="text-[#c3a565] text-xs tracking-wider uppercase"
            style={{ fontWeight: 600 }}
          >
            {benefit.label}
          </p>
        </div>

        <p
          className="text-[#faf9f7]/50 text-xs"
          style={{ fontWeight: 400 }}
        >
          {benefit.value}
        </p>
      </motion.div>
    );
  })}
</div>

        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-12 text-center"
        >
          <p className="text-[#faf9f7]/70 text-sm mb-4" style={{ fontWeight: 300 }}>
            Experience the future of legal service delivery with our intelligent CRM platform
          </p>
          <div className="flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#c3a565]" />
            <span className="text-[#c3a565] text-sm" style={{ fontWeight: 500 }}>
              Request Portal Access After Onboarding
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};